import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import type { GroupBase, OptionsOrGroups, Props as SelectProps, StylesConfig, CSSObjectWithLabel } from 'react-select';

import { getFormError } from 'constants/formErrors';

import { Option, OptionValue } from 'components/TotemSelect';
import { TotemLabel } from '../TotemLabel';

type DetailFormAsyncSelectProps<T extends OptionValue> = Omit<
    SelectProps<Option<T>, boolean, GroupBase<Option<T>>>,
    'value' | 'onChange'
> & {
    label?: string;
    sublabel?: string;
    name: string;
    defaultOptions?: Option<T>[];
    width?: string;
    required?: boolean;
    isClearable?: boolean;
    dataTest?: string;
    placeholder?: string;
    loadOptions: (inputValue: string) => Promise<OptionsOrGroups<Option<T>, GroupBase<Option<T>>>>;
};

export const DetailFormAsyncSelect = <T extends OptionValue>({
    label,
    sublabel,
    name,
    defaultOptions,
    width,
    dataTest = '',
    required = false,
    isClearable = false,
    placeholder = '',
    loadOptions,
    ...props
}: DetailFormAsyncSelectProps<T>) => {
    const {
        control,
        formState: { errors },
        watch,
    } = useFormContext();
    const {
        field: { onChange, value },
    } = useController({
        name,
        control,
        rules: { required },
    });

    const [options, setOptions] = useState<Option<T>[]>(defaultOptions || []);

    // This is a workaround to init options with form value
    useEffect(() => {
        const currentValue = watch(name);
        if (currentValue && !options.find((opt) => opt.value === currentValue)) {
            loadOptions(currentValue).then((newOptions) => {
                const flatOptions = Array.isArray(newOptions) ? newOptions : [newOptions];
                setOptions(() => [
                    ...(defaultOptions || []),
                    ...flatOptions.filter((opt): opt is Option<T> => 'value' in opt),
                ]);
            });
        } else {
            setOptions(defaultOptions || []);
        }
    }, [watch(name), loadOptions, name, options, defaultOptions]);

    const error = required && errors?.[name] ? getFormError(errors[name]?.type?.toString()) : undefined;

    const onSingleChange = (selectedOption: Option<T>) => {
        onChange(selectedOption?.value || null);
    };

    const onMultiChange = (selectedOption: readonly Option<T>[]) => {
        onChange(selectedOption?.map((option) => option.value));
    };

    const selectStyles: StylesConfig<Option<T>, boolean, GroupBase<Option<T>>> = {
        control: (base: CSSObjectWithLabel) => {
            return {
                ...base,
                minHeight: 45,
            };
        },
    };

    return (
        <div className="flex flex-col gap-[5px]" style={{ width: width || '100%' }}>
            {label || sublabel ? (
                <div className="flex flex-col">
                    {label ? <TotemLabel>{label}</TotemLabel> : null}
                    {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
                </div>
            ) : null}
            <div className="text-black relative" data-test={dataTest}>
                <AsyncSelect
                    placeholder={placeholder || 'Aucune valeur'}
                    cacheOptions
                    defaultOptions={options}
                    onChange={(selectedOption) => {
                        if (props.isMulti) {
                            onMultiChange(selectedOption as readonly Option<T>[]);
                        } else {
                            onSingleChange(selectedOption as Option<T>);
                        }
                    }}
                    closeMenuOnSelect={!props.isMulti}
                    loadOptions={loadOptions}
                    isClearable={isClearable}
                    styles={selectStyles}
                    value={options.find((opt) => opt.value === value)}
                    {...props}
                />
                {error ? (
                    <div className="absolute right-[-2px] top-[-16px] text-base text-destructive w-max">{error}</div>
                ) : null}
            </div>
        </div>
    );
};
